import React, { useState } from "react";
import { DataGrid } from "@mui/x-data-grid";
import {
  Box,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Typography,
  List,
  ListItem,
  ListItemText,
  Divider,
  Chip,
  IconButton,
  Tooltip,
} from "@mui/material";
import VisibilityIcon from "@mui/icons-material/Visibility"; // Optional icon

// --- Helper Function for Formatting Dates (Optional) ---
const formatDate = (dateString) => {
  if (!dateString) return "N/A";
  try {
    return new Date(dateString).toLocaleString();
  } catch (error) {
    return dateString; // Return original if formatting fails
  }
};

// --- Main Component ---
const EvaluationHistoryView = ({ evaluationHistoryData }) => {
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [selectedEvaluation, setSelectedEvaluation] = useState(null);

  const handleOpenDialog = (evaluation) => {
    setSelectedEvaluation(evaluation);
    setIsDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setIsDialogOpen(false);
    setSelectedEvaluation(null); // Clear selection on close
  };

  // --- DataGrid Columns Definition ---
  const columns = [
    {
      field: "actions",
      headerName: "Actions",
      sortable: false,
      filterable: false,
      disableColumnMenu: true,
      width: 130,
      renderCell: (params) => (
        <Tooltip title="View Details">
          <IconButton
            color="primary"
            size="small"
            onClick={() => handleOpenDialog(params.row)}
            aria-label="View evaluation details"
          >
            <VisibilityIcon />
          </IconButton>
        </Tooltip>
      ),
    },
    { field: "id", headerName: "History ID", width: 250 },
    {
      field: "createdAt",
      headerName: "Evaluation Date",
      width: 180,
      valueGetter: (params) => formatDate(params.row.createdAt),
    },
    {
      field: "completed_by_email",
      headerName: "Evaluated By",
      width: 200,
      // Access nested data using valueGetter
      valueGetter: (params) =>
        params.row.evaluation_snapshot?.completed_by_email || "N/A",
    },
    {
      field: "call_center_agent",
      headerName: "Agent ID",
      width: 250,
      // You might want to fetch/display agent name here if available
    },
    {
      field: "campaign_id",
      headerName: "Campaign ID",
      width: 250,
      // You might want to fetch/display campaign name here if available
    },
    {
      field: "score",
      headerName: "Score",
      type: "number",
      width: 100,
      valueGetter: (params) =>
        params.row.evaluation_snapshot?.score?.toFixed(2) || "N/A",
    },
    {
      field: "status",
      headerName: "Status",
      width: 120,
      renderCell: (params) => {
        const status = params.row.evaluation_snapshot?.status;
        if (!status) return "N/A";
        return (
          <Chip
            label={status}
            color={
              status === "Failed"
                ? "error"
                : status === "Passed"
                ? "success"
                : "default"
            }
            size="small"
          />
        );
      },
    },
    {
      field: "working_time_this_attempt_seconds",
      headerName: "Duration (s)",
      type: "number",
      width: 120,
      valueGetter: (params) =>
        params.row.evaluation_snapshot?.working_time_this_attempt_seconds ||
        "N/A",
    },
  ];

  // Ensure rows have the top-level 'id' required by DataGrid
  const rows = evaluationHistoryData.map((item) => ({
    ...item,
    // id: item.id // Already present in your data structure
  }));

  // --- Render ---
  return (
    <Box>
      <Typography variant="body1" gutterBottom>
        Evaluation History
      </Typography>
      <DataGrid
        rows={rows}
        columns={columns}
        pageSize={10}
        rowsPerPageOptions={[10, 25, 50]}
        checkboxSelection={false} // Set to true if you need checkboxes
        disableSelectionOnClick
        autoHeight // Adjusts height to content if rows < pageSize
        // For fixed height, remove autoHeight and ensure parent Box has height
      />

      {/* --- Details Dialog --- */}
      {selectedEvaluation && (
        <Dialog
          open={isDialogOpen}
          onClose={handleCloseDialog}
          maxWidth="md" // Adjust as needed ('xs', 'sm', 'md', 'lg', 'xl')
          fullWidth
          scroll="paper" // Allows content scrolling
          aria-labelledby="evaluation-details-dialog-title"
        >
          <DialogTitle id="evaluation-details-dialog-title">
            Evaluation Details - ID: {selectedEvaluation.id}
          </DialogTitle>
          <DialogContent dividers>
            {" "}
            {/* Adds dividers around content */}
            <Typography variant="h6" gutterBottom>
              Summary
            </Typography>
            <Typography variant="body2">
              <strong>Agent ID:</strong> {selectedEvaluation.call_center_agent}
            </Typography>
            <Typography variant="body2">
              <strong>Evaluated By:</strong>{" "}
              {selectedEvaluation.evaluation_snapshot?.completed_by_email ||
                "N/A"}
            </Typography>
            <Typography variant="body2">
              <strong>Score:</strong>{" "}
              {selectedEvaluation.evaluation_snapshot?.score?.toFixed(2) ??
                "N/A"}
            </Typography>
            <Typography
              variant="body2"
              sx={{ display: "flex", alignItems: "center", gap: 1 }}
            >
              <strong>Status:</strong>
              <Chip
                label={selectedEvaluation.evaluation_snapshot?.status || "N/A"}
                color={
                  selectedEvaluation.evaluation_snapshot?.status === "Failed"
                    ? "error"
                    : selectedEvaluation.evaluation_snapshot?.status ===
                      "Passed"
                    ? "success"
                    : "default"
                }
                size="small"
              />
            </Typography>
            <Typography variant="body2">
              <strong>Evaluation Date:</strong>{" "}
              {formatDate(selectedEvaluation.createdAt)}
            </Typography>
            <Typography variant="body2">
              <strong>Start Time:</strong>{" "}
              {formatDate(selectedEvaluation.start_time)}
            </Typography>
            <Typography variant="body2">
              <strong>End Time:</strong>{" "}
              {formatDate(selectedEvaluation.end_time)}
            </Typography>
            <Typography variant="body2" sx={{ mt: 1 }}>
              <strong>Overall Notes:</strong>{" "}
              {selectedEvaluation.evaluation_snapshot?.overall_notes || "None"}
            </Typography>
            <Divider sx={{ my: 2 }} />
            <Typography variant="h6" gutterBottom>
              Questions & Answers
            </Typography>
            <List dense>
              {(selectedEvaluation.evaluation_questions_and_result || []).map(
                (qa, index) => (
                  <React.Fragment key={qa.question_id || index}>
                    <ListItem alignItems="flex-start">
                      <ListItemText
                        primary={
                          <Typography variant="body1" component="span">
                            <strong>
                              {index + 1}. {qa.question_text}
                            </strong>
                          </Typography>
                        }
                        secondary={
                          <Box
                            component="span"
                            sx={{ display: "block", mt: 0.5 }}
                          >
                            <Typography
                              variant="body2"
                              component="span"
                              sx={{ display: "block" }}
                            >
                              <strong>Answer:</strong>{" "}
                              {qa.answer_value ?? "N/A"}
                              <Chip
                                label={qa.question_type}
                                size="small"
                                sx={{ ml: 1 }}
                              />
                              {qa.auto_fail_flag && (
                                <Chip
                                  label="Auto Fail"
                                  color="warning"
                                  size="small"
                                  sx={{ ml: 1 }}
                                />
                              )}
                            </Typography>
                            <Typography
                              variant="body2"
                              component="span"
                              sx={{ display: "block" }}
                            >
                              <strong>Score:</strong> {qa.score_value ?? "N/A"}{" "}
                              / {qa.max_score_value ?? "N/A"}
                              {qa.auto_fail_triggered && (
                                <Chip
                                  label="AF Triggered"
                                  color="error"
                                  size="small"
                                  sx={{ ml: 1 }}
                                />
                              )}
                            </Typography>
                            {qa.comments && (
                              <Typography
                                variant="body2"
                                component="span"
                                sx={{
                                  display: "block",
                                  fontStyle: "italic",
                                  color: "text.secondary",
                                }}
                              >
                                <strong>Comment:</strong> {qa.comments}
                              </Typography>
                            )}
                            {/* Optional: Display Data Capture differences */}
                            {qa.question_type === "Data Capture" &&
                              qa.data_capture_values &&
                              qa.data_capture_values.length > 0 && (
                                <Box
                                  sx={{
                                    mt: 1,
                                    border: "1px dashed grey",
                                    p: 1,
                                    borderRadius: 1,
                                  }}
                                >
                                  <Typography variant="caption" component="div">
                                    Data Capture Diffs:
                                  </Typography>
                                  {qa.data_capture_values.map((dc, dcIndex) => (
                                    <Typography
                                      key={dcIndex}
                                      variant="caption"
                                      component="div"
                                    >
                                      • <strong>{dc.fieldName}:</strong> "
                                      {dc.from}" → "{dc.to}"
                                    </Typography>
                                  ))}
                                </Box>
                              )}
                          </Box>
                        }
                      />
                    </ListItem>
                    {index <
                      selectedEvaluation.evaluation_questions_and_result
                        .length -
                        1 && <Divider component="li" />}
                  </React.Fragment>
                )
              )}
              {(!selectedEvaluation.evaluation_questions_and_result ||
                selectedEvaluation.evaluation_questions_and_result.length ===
                  0) && (
                <ListItem>
                  <ListItemText primary="No questions and answers recorded for this evaluation." />
                </ListItem>
              )}
            </List>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCloseDialog} color="primary">
              Close
            </Button>
          </DialogActions>
        </Dialog>
      )}
    </Box>
  );
};

export default EvaluationHistoryView;
